import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { isLikelyOnline } from '../utils/offlineCache';

/**
 * OfflineContext — single source of truth for "is the app online?"
 *
 * Why not just use `navigator.onLine`?
 *   1. iOS Safari/Capacitor lies about it — reports `true` long after
 *      the connection actually drops.
 *   2. Even when `true`, the Railway backend may be unreachable (DNS,
 *      VPN, captive portal). We need to detect *actual* reachability.
 *
 * Strategy:
 *   - Listen to `online`/`offline` events as a fast first signal.
 *   - Every 20s while the app is in foreground, ping `${API}/api/health`.
 *     On 2 consecutive failures → declare offline. On a single success
 *     → declare online again.
 *   - Expose `isOnline`, `lastChecked`, and a manual `recheck()` so the
 *     "Try again" button in the banner can force an immediate retry.
 *
 * Used by:
 *   - `<OfflineBanner />` to show/hide the warning strip
 *   - `useOfflineGate()` to disable write actions across pages
 *   - `services/api.js` to skip network attempts when known-offline and
 *     read straight from IndexedDB
 */
const OfflineContext = createContext({
  isOnline: true,
  lastChecked: null,
  recheck: () => {},
});

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PING_INTERVAL_MS = 20_000;
const PING_TIMEOUT_MS = 5_000;

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(isLikelyOnline());
  const [lastChecked, setLastChecked] = useState(null);

  const ping = useCallback(async () => {
    if (!API_URL) return false;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), PING_TIMEOUT_MS);
    try {
      // Hit `/api/health` — a cheap, public endpoint. If it doesn't
      // exist on your backend, this will return 404 which we STILL
      // count as "online" because the server is reachable.
      //
      // We deliberately DO NOT send credentials here. The probe only
      // needs to verify the backend is reachable; sending the JWT
      // cookie/header would (a) make every probe go through auth
      // middleware unnecessarily and (b) historically tripped a CORS
      // wildcard-vs-credentials bug that flagged the live production
      // domain as offline. This probe is intentionally credential-free.
      const r = await fetch(`${API_URL}/api/health`, {
        signal: ctrl.signal,
      });
      return r.status < 500;
    } catch (_) {
      return false;
    } finally {
      clearTimeout(timer);
    }
  }, []);

  const recheck = useCallback(async () => {
    const ok = await ping();
    setIsOnline(ok);
    setLastChecked(new Date());
  }, [ping]);

  useEffect(() => {
    // Fast signals
    const goOnline = () => recheck();
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    // Initial probe
    recheck();

    // Periodic heartbeat
    const interval = setInterval(() => {
      // Pause heartbeat when the tab is hidden — saves battery on
      // background tabs and prevents false-positive offline detections
      // when the throttled timer finally runs and races with a sleep.
      if (document.visibilityState !== 'visible') return;
      recheck();
    }, PING_INTERVAL_MS);

    // Recheck on tab refocus — common pattern after the phone has
    // been locked/unlocked or app was backgrounded.
    const onVisible = () => {
      if (document.visibilityState === 'visible') recheck();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, [recheck]);

  return (
    <OfflineContext.Provider value={{ isOnline, lastChecked, recheck }}>
      {children}
    </OfflineContext.Provider>
  );
};

export const useOffline = () => useContext(OfflineContext);
